/**
 * Баталгаа ба хувийн мэдээллийн нормчлол.
 *
 * ⚠️ Сольсон гутлын баталгаа шинээр эхлэх эсэх — захиалагчийн
 * асуулт №2 хүлээгдэж байна. `ExchangePolicy` тэрийг барина.
 */
export const MS_PER_DAY = 86_400_000;
/** Борлуулах мөчид баталгаа дуусах огноог тооцно. */
export function calcWarrantyEnd(soldAt, warrantyDays) {
    if (!Number.isInteger(warrantyDays) || warrantyDays < 0) {
        throw new Error(`calcWarrantyEnd: warrantyDays эерэг бүхэл байх ёстой, ирсэн: ${warrantyDays}`);
    }
    return new Date(soldAt.getTime() + warrantyDays * MS_PER_DAY);
}
/**
 * Item-ийн хүчинтэй баталгааны хоног.
 * Нэгж дээр override байвал тэр, эс бөгөөс загварынх.
 */
export function effectiveWarrantyDays(itemOverride, modelDays) {
    return itemOverride ?? modelDays;
}
/**
 * Худалдагчийн сонгож болох баталгааны хугацаа.
 *
 * ЯАГААД ЧӨЛӨӨТЭЙ ШИВЭХГҮЙ ВЭ
 *  `order_lines.warranty_days` нь хөлддөг (CLAUDE.md №2) — алдаатай
 *  шивсэн утга 10 жил хэвээр үлдэнэ, засах зам байхгүй. Хаалттай
 *  жагсаалт нь тэр эрсдэлийг арилгана. Мөн `warranty_terms` нөхцөлийн
 *  хувилбар нь загвараас гардаг тул хугацаа нь тэр нөхцөлийн доторх
 *  СОНГОЛТ болохоос тусдаа гэрээ биш.
 *
 * ⚠️ Жил = 365 хоног. `warranty_days` нь Int, `calcWarrantyEnd` нь
 *    хоногоор бодно — өдрийн нарийвчлал хангалттай, харин хуанлийн
 *    жилээс 10 жилд ~2 хоног зөрнө. Загварын `warrantyDays` ч ижил
 *    хэмжүүртэй тул хоёр эх сурвалж нийцнэ.
 */
export const WARRANTY_PRESETS = [
    { years: 1, days: 365 },
    { years: 2, days: 730 },
    { years: 3, days: 1095 },
    { years: 5, days: 1825 },
    { years: 10, days: 3650 },
];
export const WARRANTY_PRESET_DAYS = WARRANTY_PRESETS.map((p) => p.days);
export function isWarrantyPreset(days) {
    return WARRANTY_PRESET_DAYS.includes(days);
}
/**
 * Загварын анхдагч хоногт хамгийн ойр СОНГОЛТ.
 *
 * Дэлгэц нээгдэхэд аль товч идэвхтэй байхыг шийднэ: загвар 1095
 * хоногтой бол «3 жил» өмнөөс сонгогдоно, ажилтан зөвхөн өөрчлөх
 * үедээ хүрнэ. Тэнцүү зөрүүтэй бол УРТ нь — баталгааг санамсаргүй
 * богиносгосноос уртасгасан нь дээр.
 */
export function nearestWarrantyPreset(days) {
    let best = WARRANTY_PRESETS[0].days;
    for (const p of WARRANTY_PRESETS) {
        if (Math.abs(p.days - days) <= Math.abs(best - days))
            best = p.days;
    }
    return best;
}
export function warrantyStatus(endsAt, now = new Date()) {
    const ms = endsAt.getTime() - now.getTime();
    return {
        state: ms > 0 ? 'ACTIVE' : 'EXPIRED',
        endsAt,
        daysRemaining: Math.max(0, Math.ceil(ms / MS_PER_DAY)),
    };
}
export const DEFAULT_EXCHANGE_POLICY = 'CONTINUE';
export function calcExchangeWarrantyEnd(originalEndsAt, exchangedAt, warrantyDays, policy = DEFAULT_EXCHANGE_POLICY) {
    return policy === 'RESTART'
        ? calcWarrantyEnd(exchangedAt, warrantyDays)
        : originalEndsAt;
}
/**
 * Зургийг БҮРМӨСӨН устгах огноо.
 *
 * ШИЙДВЭР (2026-09-04) — 2026-08-21-ний шийдвэрийг ОРЛОНО.
 *  Өмнө нь баталгаа дуусаад 2 жилийн дараа зургийг 2000px/q0.85 болгож
 *  БУУЛГАДАГ байсан (сонголт B). Эзэмшигч одоо «баталгаа дуусахад
 *  зургийг хасч, зөвхөн хэнд хэдээр ямар гутал зарсныг үлдээ» гэж
 *  шийдсэн — өөрөөр хэлбэл сонголт C. Буулгах алхам утгагүй болсон:
 *  устгалт нь түүнээс ӨМНӨ болно.
 *
 *  6 сарын нөөц нь буцаалт, баталгааны нэхэмжлэл дуусах өдрийн
 *  ойролцоо хоцорч ирдэгээс — маргаан гарахад нотолгоо байна.
 *
 * ⚠️ `order_lines` нь ХӨНДӨГДӨХГҮЙ (CLAUDE.md №2, №5). `snapshot`
 *    доторх `photoKeys` нь устсан түлхүүр рүү заасан хэвээр үлдэнэ —
 *    зориуд: борлуулах мөчид ямар зураг байсныг тэмдэглэсэн бичлэг нь
 *    зураг өөрөө устсаны дараа ч баримт хэвээр.
 */
export const PHOTO_PURGE_MONTHS_AFTER_WARRANTY = 6;
export function photoPurgeAfter(warrantyEndsAt) {
    const d = new Date(warrantyEndsAt);
    d.setMonth(d.getMonth() + PHOTO_PURGE_MONTHS_AFTER_WARRANTY);
    return d;
}
// ------------------------------------------------------------
//  Регистрийн дугаар
// ------------------------------------------------------------
/**
 * Иргэний регистр: 2 кирилл үсэг + 8 цифр (`УБ12345678`).
 *
 * ⚠️ Ө, Ү нь `cyrillic-ext`-д байдаг (CLAUDE.md §Дизайн систем) —
 *    регистрт бас тохиолдоно (Өвөрхангай → «ӨЛ»), тиймээс мужийг
 *    А-Я гэж бичихгүй, Ө/Ү-г ТУСАД нь нэмнэ.
 */
const REGISTRY_LETTER = '[А-ЯЁӨҮ]';
const REGISTRY_RE = new RegExp(`^${REGISTRY_LETTER}{2}\\d{8}$`);
/** Том үсэг болгож, зай/зураас хасна. Хүчингүй бол null. */
export function normalizeRegistryNo(raw) {
    if (!raw)
        return null;
    const cleaned = raw.replace(/[\s-]/g, '').toUpperCase();
    return REGISTRY_RE.test(cleaned) ? cleaned : null;
}
export function isValidRegistryNo(value) {
    return !!value && REGISTRY_RE.test(value);
}
// ------------------------------------------------------------
//  Утасны дугаар
// ------------------------------------------------------------
/**
 * Монголын дугаарыг +976XXXXXXXX болгоно.
 * Хүчингүй бол null. DB CHECK нь эцсийн хана.
 */
export function normalizePhone(raw) {
    if (!raw)
        return null;
    const digits = raw.replace(/[^\d]/g, '');
    // 99112233
    if (/^\d{8}$/.test(digits))
        return `+976${digits}`;
    // 97699112233
    if (/^976\d{8}$/.test(digits))
        return `+${digits}`;
    // 0097699112233
    if (/^00976\d{8}$/.test(digits))
        return `+${digits.slice(2)}`;
    return null;
}
export function isValidMnPhone(phone) {
    return !!phone && /^\+976\d{8}$/.test(phone);
}
// ------------------------------------------------------------
//  Нийтийн токен (QR)
// ------------------------------------------------------------
/**
 * `publicToken` нь `serial`-аас ТУСДАА байх ёстой.
 * Эс тэгвээс хэн ч дугаараа нэмээд бусдын гуталын мэдээллийг харна.
 * Урт 22 тэмдэгт, тодорхойгүй тэмдэгт (0/O, 1/l/I) хассан.
 */
const TOKEN_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const PUBLIC_TOKEN_LENGTH = 22;
export function generatePublicToken(randomBytes) {
    const bytes = randomBytes(PUBLIC_TOKEN_LENGTH);
    let out = '';
    for (let i = 0; i < PUBLIC_TOKEN_LENGTH; i++) {
        out += TOKEN_ALPHABET[bytes[i] % TOKEN_ALPHABET.length];
    }
    return out;
}
//# sourceMappingURL=warranty.js.map